import { useEffect, useState, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import '../translations.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "YYYY-MM-DD" || stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY") {
    return stored;
  }
  return "YYYY-MM-DD";
};

const formatLabelDate = (isoDate) => {
  if (!isoDate) return "";
  const d = dayjs(isoDate);
  if (!d.isValid()) return isoDate;
  const pattern = getUserDateFormat();
  return d.format(pattern);
};

const ChatHistoryLineChart = ({
  chatData,
  startDate,
  endDate,
  dataType = "unique_chats",
  title,
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });

  const processChatData = useCallback(() => {
    if (!startDate || !endDate) {
      console.error("Start date and end date are required");
      return;
    }

    const labels = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split('T')[0];
      labels.push(dateString);
      currentDate = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
    }

    const botNameMapping = {};
    chatData.forEach((chat) => {
      if (chat.bot_id && chat.bot_name) {
        botNameMapping[chat.bot_id] = chat.bot_name;
      }
    });

    const botData = {};
    chatData.forEach((chat) => {
      const chatDateObj = new Date(chat.timestamp);
      chatDateObj.setUTCHours(0, 0, 0, 0);
      if (chatDateObj < start || chatDateObj > end) {
        return;
      }
      const chatDate = chatDateObj.toISOString().split('T')[0];
      const botId = chat.bot_id;

      if (!botData[botId]) {
        botData[botId] = {};
        labels.forEach((date) => {
          botData[botId][date] = dataType === "unique_chats" ? new Set() : 0;
        });
      }

      if (dataType === "unique_chats") {
        if (!(chatDate in botData[botId])) {
          console.warn(`Unexpected chatDate "${chatDate}" not in labels. Skipping.`);
          return;
        }
        botData[botId][chatDate].add(chat.session_id);
      }
       else if (dataType === "total_user_input" && chat.type === "user_input") {
        botData[botId][chatDate] += 1;
      }
    });

    const colorPalette = [
      '#5616ea',
      '#ba3800',
      '#baa800',
      '#82ba00',
      '#9500ba'
    ];

    const datasets = [];
    const uniqueBotIds = Object.keys(botData).sort();
    uniqueBotIds.forEach((botId, index) => {
      const data = labels.map((date) => {
        if (dataType === "unique_chats") {
          return botData[botId][date].size;
        } else {
          return botData[botId][date];
        }
      });
      const color = colorPalette[index % colorPalette.length];
      datasets.push({
        label: botNameMapping[botId] || `Bot ${botId}`,
        data: data,
        backgroundColor: color + '1A',
        borderColor: color,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      });
    });

    setChartData({
      labels,
      datasets,
    });
  }, [chatData, startDate, endDate, dataType]);

  useEffect(() => {
    processChatData();
  }, [chatData, startDate, endDate, processChatData]);

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 20,
          usePointStyle: false,
          color: '#333',
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 15,
          maxRotation: 30,
          minRotation: 30,
          callback: function(value) {
            const rawLabel = this.getLabelForValue(value);
            return formatLabelDate(rawLabel);
          },
        },
      },
      y: {
        grid: {
          display: true,
        },
        ticks: {
          stepSize: 1,
        },
        min: 0,
      },
    },
  };

  return (
    <div className='charts-container'>
      <div className='charts-title'>
        {t(title)}
      </div>
      <div className='charts-graph'>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ChatHistoryLineChart;
